import { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { Props as FormattedMessageProps } from 'react-intl/src/components/message';

import { getYear } from 'date-fns';

import { getBannerImageUrl, getCDNOptimizedImage, Tag } from '@arrived/common';
import { AssetType, LeaseStatus, LeverageFilter, OfferingSearchQuery } from '@arrived/models';
import { useGetKpisQuery } from '@arrived/queries';

export interface FeaturedFilter {
  label: FormattedMessageProps;
  labelIcon?: string;
  /**
   * The filter options that will be applied
   */
  filter: OfferingSearchQuery | Record<string, never>;
  /**
   * Top section to describe what the filter is, desktop
   * can include a banner image.
   */
  banner: {
    title: FormattedMessageProps;
    description?: FormattedMessageProps;
    children?: ReactNode;
    image?: {
      src: string;
      alt: string;
    };
  };
}

export const AllPropertiesDescription = () => {
  const arrivedKpisState = useGetKpisQuery();

  if (!arrivedKpisState.isSuccess) {
    return (
      <FormattedMessage
        id="all-properties-banner-description-short"
        defaultMessage="Our team selectively purchases the top 1% of properties we source, making each hand-picked property an inspired opportunity."
      />
    );
  }

  return (
    <FormattedMessage
      id="all-properties-banner-description"
      defaultMessage="Over {value} in real estate funded across {homes} properties... and counting! Our team selectively purchases the top 1% of properties we source, making each hand-picked property an inspired opportunity."
      values={{
        value: (
          <FormattedNumber
            currency="USD"
            maximumFractionDigits={0}
            minimumFractionDigits={0}
            notation="compact"
            style="currency"
            value={arrivedKpisState.data.fundedPropertyValue}
          />
        ),
        homes: arrivedKpisState.data.fundedPropertiesCount,
      }}
    />
  );
};

export const FeaturedFilters: FeaturedFilter[] = [
  {
    label: { id: 'all-properties-filter-label', defaultMessage: 'All' },
    filter: {},
    banner: {
      title: { id: 'all-properties-filter-title', defaultMessage: 'All Properties' },
      image: {
        src: getCDNOptimizedImage({
          imgUrl: getBannerImageUrl('street-blocks-2.png'),
          width: '570px',
          height: '320px',
        }),
        alt: 'All Properties',
      },
    },
  },
  {
    label: { id: 'vacation-rentals-filter-label', defaultMessage: 'Vacation Rentals' },
    labelIcon: 'palm-tree',
    filter: { assetType: { eq: [AssetType.STR] } },
    banner: {
      title: { id: 'vacation-rentals-filter-title', defaultMessage: 'Vacation Rentals' },
      description: {
        id: 'vacation-rentals-filter-description',
        defaultMessage:
          'Furnished properties rented out for shorter stays (days, weeks, or months) on platforms like Airbnb & VRBO. Diversify your portfolio with vacation rentals!',
      },
      image: {
        src: getCDNOptimizedImage({
          imgUrl: getBannerImageUrl('Vacation Rentals.png'),
          width: '570px',
          height: '320px',
        }),

        alt: 'Vacation Rentals',
      },
    },
  },
  {
    label: { id: 'leveraged-filter-label', defaultMessage: 'Leveraged' },
    filter: { leverage: { eq: [LeverageFilter.MEDIUM, LeverageFilter.HIGH] } },
    banner: {
      title: { id: 'leveraged-filter-title', defaultMessage: 'Leveraged' },
      description: {
        id: 'leveraged-description',
        defaultMessage:
          'Properties with leverage are partially funded with a bank loan. Financial leverage provides magnification on the potential investment returns.',
      },
      image: {
        src: getCDNOptimizedImage({ imgUrl: getBannerImageUrl('Leveraged.png'), width: '570px', height: '320px' }),
        alt: 'Leveraged',
      },
    },
  },
  {
    label: { id: 'appreciation-focused', defaultMessage: 'Appreciation Focused' },
    filter: { tagId: { eq: [Tag.APPRECIATION_FOCUSED] } },
    banner: {
      title: { id: 'appreciation-focused', defaultMessage: 'Appreciation Focused' },
      description: {
        id: 'appreciation-focused-banner-message',
        defaultMessage:
          'Rental properties in markets with higher historical appreciation. These properties may derive a larger portion of total returns from property value growth.',
      },
      image: {
        src: getCDNOptimizedImage({ imgUrl: getBannerImageUrl('Appreciation.png'), width: '570px', height: '320px' }),
        alt: 'Appreciation Focused',
      },
    },
  },
  {
    label: { id: 'great-schools', defaultMessage: 'Great Schools' },
    filter: { tagId: { eq: [Tag.GREAT_SCHOOLS] } },
    banner: {
      title: { id: 'great-schools', defaultMessage: 'Great Schools' },
      description: {
        id: 'great-schools-banner-message',
        defaultMessage:
          'Homes in great school districts. Properties in higher rated school districts may enhance property values and attract more families.',
      },
      image: {
        src: getCDNOptimizedImage({ imgUrl: getBannerImageUrl('TopSchools.png'), width: '570px', height: '320px' }),
        alt: 'Top Schools',
      },
    },
  },
  {
    label: { id: 'leased-homes-filter-label', defaultMessage: 'Rented' },
    filter: { leaseStatus: { eq: [LeaseStatus.OCCUPIED] } },
    banner: {
      title: { id: 'leased-homes-filter-title', defaultMessage: 'Rented Homes' },
      description: {
        id: 'leased-homes-description',
        defaultMessage:
          'These homes are currently rented out and are occupied by tenants who have signed a long-term lease of 12 or more months.',
      },
      image: {
        src: getCDNOptimizedImage({
          imgUrl: getBannerImageUrl('street-blocks-2.png'),
          width: '570px',
          height: '320px',
        }),
        alt: 'Leased Homes',
      },
    },
  },
  {
    label: { id: 'newly-built-filter-label', defaultMessage: 'Newly Built' },
    filter: { yearBuilt: { gte: getYear(new Date()) - 2 } },
    banner: {
      title: { id: 'newly-built-filter-title', defaultMessage: 'Newly Built' },
      description: {
        id: 'newly-built-description',
        defaultMessage:
          'Homes built within the last two years. Newer properties are highly attractive to tenants and investors, and are likely to have lower future expenses.',
      },
      image: {
        src: getCDNOptimizedImage({ imgUrl: getBannerImageUrl('NewlyBuilt.png'), width: '570px', height: '320px' }),
        alt: 'Newly Built',
      },
    },
  },
];
